/**
 * The client portal's Notices page calls (R190, decision 1010 item 1; docs/specs/cloud/alerts.md §11;
 * 20261103233000_client_notices.sql): the person's own choices per kind for their client organisation, the notices of
 * the last 30 days, and the link a notice opens. RLS decides: a person reads and writes only their own choices, for an
 * organisation they belong to, and reads only their own organisation's notices. Push on this device and a report's
 * email reuse the alerts' calls (`alerts/api.ts`) and the report email's (`setup/api.ts`).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { CLIENT_NOTICE_RECENT_DAYS, type ClientNoticePreference } from '@teslyn/domain';

const fail = (e: { message: string } | null): void => {
  if (e) throw new Error(e.message);
};

/** The person's own choices for the organisation, one per kind they changed. */
export async function loadNoticeChoices(
  sb: SupabaseClient,
  userId: string,
  clientOrgId: string,
): Promise<ClientNoticePreference[]> {
  const { data, error } = await sb
    .from('client_notice_preference')
    .select('user_id, kind, push, email')
    .eq('user_id', userId)
    .eq('client_org_id', clientOrgId);
  fail(error);
  return (data ?? []) as ClientNoticePreference[];
}

/** Save one kind's choice (null: the kind's default). A report's email is the report email's (never stored here). */
export async function saveNoticeChoice(
  sb: SupabaseClient,
  userId: string,
  clientOrgId: string,
  choice: { readonly kind: string; readonly push: boolean | null; readonly email: boolean | null },
): Promise<void> {
  const { error } = await sb.from('client_notice_preference').upsert(
    {
      user_id: userId,
      client_org_id: clientOrgId,
      kind: choice.kind,
      push: choice.push,
      email: choice.kind === 'report_published' ? null : choice.email,
    },
    { onConflict: 'user_id,client_org_id,kind' },
  );
  fail(error);
}

export interface RecentNotice {
  readonly id: string;
  readonly kind: string;
  /** Null only for a contract's approval made before its project (R191). */
  readonly project_id: string | null;
  readonly occurred_at: string;
  readonly detail: Record<string, unknown>;
  readonly status: string;
}

/** The organisation's notices of the last 30 days, newest first (cancelled ones were never sent: left out). */
export async function loadRecentNotices(
  sb: SupabaseClient,
  clientOrgId: string,
  nowMs: number = Date.now(),
): Promise<RecentNotice[]> {
  const since = new Date(nowMs - CLIENT_NOTICE_RECENT_DAYS * 86_400_000).toISOString();
  const { data, error } = await sb
    .from('client_notice')
    .select('id, kind, project_id, occurred_at, detail, status')
    .eq('client_org_id', clientOrgId)
    .neq('status', 'cancelled')
    .gte('occurred_at', since)
    .order('occurred_at', { ascending: false })
    .limit(50);
  fail(error);
  return (data ?? []) as RecentNotice[];
}

/** `?page=<page>&project=<id>`: the project a link opens on that page, or null. */
function pageProjectLink(search: string, page: string): string | null {
  const q = new URLSearchParams(search);
  const id = q.get('project');
  return q.get('page') === page && id && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
}

/** `?page=projects&project=<id>`: the project a client notice opens, or null. */
export function noticeProjectLink(search: string): string | null {
  return pageProjectLink(search, 'projects');
}

/**
 * `?page=reports&project=<id>`: the project Reports opens on, from a report email, a client notice of a new report or
 * the "Email…" link of a report (R198), or null.
 */
export function reportsProjectLink(search: string): string | null {
  return pageProjectLink(search, 'reports');
}

/** `?page=safety-register&project=<id>`: the project a register notice opens the client's Safety register on (R220). */
export function registerProjectLink(search: string): string | null {
  return pageProjectLink(search, 'safety-register');
}
